/**
 * useThemeMode — the live light/dark state of the document.
 *
 * The portal toggles dark mode by adding/removing `.dark` on
 * `<html>` (see `_layout.tsx` and `settings/index.tsx`); there is no shared
 * theme store, so components that need to *render differently* per theme —
 * rather than just restyle via a `dark:` class — have nothing to subscribe to.
 *
 * This subscribes to the class attribute itself, so it stays correct no matter
 * which code path flipped the theme (toggle, settings page, or the
 * `prefers-color-scheme` bootstrap on mount).
 */
import { useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark';

function subscribe(onStoreChange: () => void): () => void {
  const observer = new MutationObserver(onStoreChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
  return () => observer.disconnect();
}

const getSnapshot = (): ThemeMode =>
  document.documentElement.classList.contains('dark') ? 'dark' : 'light';

/** Light is the correct pre-hydration default — `index.html` ships no `.dark`. */
const getServerSnapshot = (): ThemeMode => 'light';

export function useThemeMode(): ThemeMode {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export default useThemeMode;
